import { AuthError, get, handleAuthFailure, post, put, del } from '../lib/request';
import {
  LoginResponse, AccountDetail, Order, PaginatedResponse,
  AdminStats, Card, SystemSettings, ApiResponse, OrderAnalytics,
  Item, ItemCardBinding, AIReplySettings, ShippingRule, ReplyRule, DefaultReply,
  ChatMessage, ChatMessagesResponse, ChatSession, ChatSessionsResponse
} from '../types';

const toAbsoluteImageUrl = (url?: unknown): string => {
  if (typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) return trimmed;
  return trimmed;
};

const parseJsonObject = (value?: unknown): Record<string, any> => {
  if (!value) return {};
  if (typeof value === 'object') return value as Record<string, any>;
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const firstNonEmpty = (...values: unknown[]): string => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
};

const firstNumericAmount = (...values: unknown[]): string => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (!text || text === 'N/A') continue;
    if (/-?\d+(?:\.\d+)?/.test(text.replace(/,/g, ''))) return text;
  }
  return '';
};

const resolveAccountName = (item: any): string => firstNonEmpty(
  item.nickname,
  item.nick_name,
  item.display_name,
  item.name,
  item.account_name,
  item.tracknick,
  item.username,
  item.remark,
  item.id ? `账号 ${String(item.id).substring(0, 6)}` : '未命名账号'
);

const extractItemImage = (item: any): string => {
  const detail = parseJsonObject(item.item_detail_parsed || item.item_detail);
  const picInfo = item.pic_info || item.picInfo || detail.pic_info || detail.picInfo || {};

  return toAbsoluteImageUrl(
    item.item_image ||
    item.main_image ||
    item.image_url ||
    item.pic_url ||
    picInfo.picUrl ||
    picInfo.pic_url ||
    picInfo.url ||
    picInfo.imageUrl ||
    detail.item_image ||
    detail.main_image ||
    detail.image_url
  );
};

const normalizeItem = (item: any): Item => ({
  ...item,
  item_image: extractItemImage(item),
  is_multi_spec: Boolean(item.is_multi_spec),
  is_multi_qty_ship: Boolean(item.is_multi_qty_ship ?? item.multi_quantity_delivery),
  auto_relist_enabled: Boolean(item.auto_relist_enabled),
  card_binding: item.card_binding ? normalizeItemCardBinding(item.card_binding) : null,
  card_bindings: Array.isArray(item.card_bindings) ? item.card_bindings.map(normalizeItemCardBinding) : [],
});

const normalizeItemCardBinding = (item: any): ItemCardBinding => ({
  ...item,
  id: Number(item.id),
  card_id: Number(item.card_id),
  enabled: Boolean(item.enabled),
  card_enabled: item.card_enabled === undefined ? undefined : Boolean(item.card_enabled),
  card_is_multi_spec: item.card_is_multi_spec === undefined ? undefined : Boolean(item.card_is_multi_spec),
  item_is_multi_spec: item.item_is_multi_spec === undefined ? undefined : Boolean(item.item_is_multi_spec),
  multi_quantity_delivery: item.multi_quantity_delivery === undefined ? undefined : Boolean(item.multi_quantity_delivery),
});

const normalizeOrder = (order: any): Order => {
  const detail = parseJsonObject(order.item_detail_parsed || order.item_detail);
  const buyer = order.buyer || order.buyer_info || order.buyerInfo || {};
  const itemPrice = firstNonEmpty(order.item_price, order.price, detail.price_text, detail.price);
  return {
    ...order,
    id: firstNonEmpty(order.id, order.order_id),
    order_id: firstNonEmpty(order.order_id, order.id),
    buyer_id: firstNonEmpty(order.buyer_id, order.buyerId, order.buyer_user_id),
    buyer_name: firstNonEmpty(
      order.buyer_name,
      order.buyerName,
      order.buyer_nickname,
      order.buyerNick,
      order.buyer_nick,
      order.buyerNickName,
      buyer.name,
      buyer.nickName,
      buyer.nickname,
      buyer.nick
    ),
    seller_nickname: firstNonEmpty(order.seller_nickname, order.seller_name, order.account_name, order.cookie_name),
    account_name: firstNonEmpty(order.account_name, order.seller_nickname, order.seller_name, order.cookie_name),
    item_title: firstNonEmpty(order.item_title, order.title, detail.title),
    item_price: itemPrice,
    item_image: extractItemImage({ ...order, item_detail_parsed: detail }),
    amount: firstNumericAmount(
      order.amount,
      order.actual_amount,
      order.actualAmount,
      order.pay_amount,
      order.payAmount,
      order.paid_amount,
      order.paidAmount,
      order.total_amount,
      order.totalAmount,
      order.price,
      itemPrice
    ),
  };
};

const throwHttpError = (response: Response, result: any, fallback: string): never => {
  const message = result?.detail || result?.message || `${fallback} (${response.status})`;
  if (response.status === 401 || response.status === 403) {
    handleAuthFailure(response.status, message);
    throw new AuthError(message, response.status);
  }
  throw new Error(message);
};

// Auth
export const login = async (data: { username?: string; password?: string; email?: string; verification_code?: string }): Promise<LoginResponse> => {
  return post('/login', data);
};

export const verifyToken = async (): Promise<{ authenticated: boolean; user_id?: number; username?: string }> => {
  return get('/verify');
};

export const logout = async (): Promise<ApiResponse> => {
  return post('/logout', {});
};

export const changePassword = async (currentPassword: string, newPassword: string): Promise<ApiResponse> => {
  return post('/change-password', { current_password: currentPassword, new_password: newPassword });
};

// Accounts
export const getAccountDetails = async (): Promise<AccountDetail[]> => {
  const data = await get<any[]>('/cookies/details');
  // Map backend fields to UI fields if necessary
  return data.map(item => ({
    id: item.id,
    value: item.value,
    cookie: item.value,
    enabled: item.enabled,
    auto_confirm: item.auto_confirm,
    remark: item.remark,
    note: item.remark,
    pause_duration: item.pause_duration,
    username: item.username,
    login_password: item.login_password,
    show_browser: item.show_browser,
    nickname: resolveAccountName(item),
    name: resolveAccountName(item),
    account_name: resolveAccountName(item),
    avatar_url: `https://api.dicebear.com/7.x/avataaars/svg?seed=${item.id}`, // Placeholder avatar
    ai_enabled: false, // 需要从AI设置API获取
  }));
};

export const generateQRLogin = async (): Promise<{ success: boolean; session_id?: string; qr_code_url?: string; message?: string }> => {
  return post('/qr-login/generate');
};

export const checkQRLoginStatus = async (sessionId: string): Promise<any> => {
  return get(`/qr-login/check/${sessionId}`);
};

export const updateAccountStatus = async (id: string, enabled: boolean): Promise<any> => {
  return put(`/cookies/${id}/status`, { enabled });
};

export const deleteAccount = async (id: string): Promise<any> => {
  return del(`/cookies/${id}`);
};

export const updateAccountRemark = async (id: string, remark: string): Promise<any> => {
  return put(`/cookies/${id}/remark`, { remark });
};

export const updateAccountAutoConfirm = async (id: string, autoConfirm: boolean): Promise<any> => {
  return put(`/cookies/${id}/auto-confirm`, { auto_confirm: autoConfirm });
};

export const updateAccountPauseDuration = async (id: string, pauseDuration: number): Promise<any> => {
  return put(`/cookies/${id}/pause-duration`, { pause_duration: pauseDuration });
};

export const updateAccountCookie = async (id: string, value: string): Promise<any> => {
  return put(`/cookies/${id}`, { id, value });
};

export const updateAccountLoginInfo = async (id: string, data: {
  username?: string;
  login_password?: string;
  show_browser?: boolean;
}): Promise<any> => {
  return put(`/cookies/${id}/login-info`, data);
};

export const getAllAISettings = async (): Promise<Record<string, AIReplySettings>> => {
  return get('/ai-reply-settings');
};

// Orders
export const getOrders = async (
  cookieId?: string,
  status?: string,
  page: number = 1,
  pageSize: number = 20
): Promise<PaginatedResponse<Order>> => {
  const params: any = { page, page_size: pageSize };
  if (cookieId) params.cookie_id = cookieId;
  if (status && status !== 'all') params.status = status;

  const res = await get<any>('/api/orders', params);

  // Handle backend response variations
  const orders = res.orders || res.data || [];
  return {
    success: true,
    data: orders.map(normalizeOrder),
    total: res.total || orders.length,
    page: res.page || page,
    page_size: res.page_size || pageSize,
    total_pages: res.total_pages || 1
  };
};

export const getOrderDetail = async (orderId: string): Promise<{ success: boolean; data?: Order }> => {
  const result = await get<{ order?: Order; data?: Order }>(`/api/orders/${orderId}`);
  const order = result.order || result.data;
  return {
    success: true,
    data: order ? normalizeOrder(order) : undefined
  };
};

export const updateOrder = async (orderId: string, data: Partial<Order>): Promise<ApiResponse> => {
  return put(`/api/orders/${orderId}`, data);
};

export const deleteOrder = async (orderId: string): Promise<ApiResponse> => {
  return del(`/api/orders/${orderId}`);
};

export const syncOrders = async (cookieId?: string, status?: string): Promise<any> => {
  const formData = new FormData();
  if (cookieId) formData.append('cookie_id', cookieId);
  if (status) formData.append('status', status);

  // 使用 fetch 来发送 FormData
  const token = localStorage.getItem('auth_token');
  const response = await fetch('/api/orders/refresh', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData
  });
  const result = await response.json().catch(() => null);
  if (!response.ok) {
    throwHttpError(response, result, '刷新失败');
  }
  return result;
};

export const syncOrdersFromAccount = async (options?: {
  cookieId?: string;
  maxPages?: number;
  maxOrders?: number;
}): Promise<any> => {
  const formData = new FormData();
  if (options?.cookieId) formData.append('cookie_id', options.cookieId);
  if (options?.maxPages) formData.append('max_pages', String(options.maxPages));
  if (options?.maxOrders) formData.append('max_orders', String(options.maxOrders));

  const token = localStorage.getItem('auth_token');
  const response = await fetch('/api/orders/sync-from-account', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData
  });
  const result = await response.json().catch(() => null);
  if (!response.ok) {
    throwHttpError(response, result, '订单同步失败');
  }
  return result;
};

export const syncSingleOrder = async (orderId: string): Promise<any> => {
  return post(`/api/orders/${orderId}/refresh`);
};

export const manualShipOrder = async (orderIds: string[], shipMode: 'status_only' | 'full_delivery', content?: string): Promise<any> => {
    return post('/api/orders/manual-ship', {
        order_ids: orderIds,
        ship_mode: shipMode,
        custom_content: content
    });
}

export const importOrders = async (data: Partial<Order>[] | FormData): Promise<any> => {
  const isFormData = data instanceof FormData;
  const response = await fetch('/api/orders/import', {
    method: 'POST',
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    },
    body: isFormData ? data : JSON.stringify(data)
  });
  const result = await response.json().catch(() => null);
  if (!response.ok) {
    throwHttpError(response, result, '订单导入失败');
  }
  return result;
}

// Stats
export const getAdminStats = async (): Promise<AdminStats> => {
  return get('/admin/stats');
};

export const getOrderAnalytics = async (daysOrParams: number | {start_date: string; end_date: string} = 7): Promise<OrderAnalytics> => {
    let params: {start_date: string; end_date: string};

    if (typeof daysOrParams === 'number') {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - daysOrParams);
        params = {
            start_date: startDate.toISOString().split('T')[0],
            end_date: endDate.toISOString().split('T')[0]
        };
    } else {
        params = daysOrParams;
    }

    return get('/analytics/orders', params);
}

export const getValidOrders = async (dateRange: {start_date: string; end_date: string}): Promise<Order[]> => {
    const res = await get<any>('/analytics/orders/valid', {
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
    });
    return (res.orders || []).map(normalizeOrder);
}

// Chat Center
export const getChatSessions = async (params?: {
  cookie_id?: string;
  q?: string;
  limit?: number;
}): Promise<ChatSessionsResponse> => {
  return get<ChatSessionsResponse>('/api/chat/sessions', params);
};

export const getChatMessages = async (
  cookieId: string,
  chatId: string,
  limit: number = 100
): Promise<ChatMessagesResponse> => {
  return get<ChatMessagesResponse>(`/api/chat/sessions/${encodeURIComponent(cookieId)}/${encodeURIComponent(chatId)}/messages`, { limit });
};

export const markChatRead = async (cookieId: string, chatId: string): Promise<ApiResponse> => {
  return post(`/api/chat/sessions/${encodeURIComponent(cookieId)}/${encodeURIComponent(chatId)}/read`, {});
};

export const sendChatMessage = async (
  cookieId: string,
  chatId: string,
  toUserId: string,
  message: string
): Promise<ApiResponse & { data?: ChatMessage; session?: ChatSession }> => {
  return post(`/api/chat/sessions/${encodeURIComponent(cookieId)}/${encodeURIComponent(chatId)}/send`, {
    to_user_id: toUserId,
    message,
  });
};

export const sendChatImageMessage = async (
  cookieId: string,
  chatId: string,
  toUserId: string,
  image: File
): Promise<ApiResponse & { data?: ChatMessage; session?: ChatSession }> => {
  const buildFormData = () => {
    const formData = new FormData();
    formData.append('to_user_id', toUserId);
    formData.append('image', image);
    return formData;
  };
  const basePath = `/api/chat/sessions/${encodeURIComponent(cookieId)}/${encodeURIComponent(chatId)}`;
  try {
    return await post(`${basePath}/send-image`, buildFormData());
  } catch (error) {
    const message = error instanceof Error ? error.message : '';
    if (/Method Not Allowed|请求失败 \(405\)/i.test(message)) {
      try {
        return await post(`${basePath}/send_image`, buildFormData());
      } catch (fallbackError) {
        const fallbackMessage = fallbackError instanceof Error ? fallbackError.message : '';
        if (/Method Not Allowed|请求失败 \(405\)/i.test(fallbackMessage)) {
          throw new Error('图片发送接口未加载，请重启后端服务后再发送图片');
        }
        throw fallbackError;
      }
    }
    throw error;
  }
};

export const connectChatEvents = (
  onEvent: (event: any) => void,
  onError?: (error: Error) => void
): (() => void) => {
  const controller = new AbortController();

  const run = async () => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch('/api/chat/events', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: controller.signal,
    });

    if (!response.ok || !response.body) {
      const message = `消息流连接失败 (${response.status})`;
      if (response.status === 401 || response.status === 403) {
        handleAuthFailure(response.status, message);
        throw new AuthError(message, response.status);
      }
      throw new Error(message);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (!controller.signal.aborted) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() || '';

      chunks.forEach((chunk) => {
        const data = chunk
          .split('\n')
          .filter(line => line.startsWith('data:'))
          .map(line => line.slice(5).trimStart())
          .join('\n');

        if (!data) return;
        try {
          onEvent(JSON.parse(data));
        } catch (error) {
          console.warn('Failed to parse chat event', error);
        }
      });
    }
  };

  run().catch((error) => {
    if (!controller.signal.aborted) {
      onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  });

  return () => controller.abort();
};

// Cards
export const getCards = async (): Promise<Card[]> => {
  const res = await get<any>('/cards');
  return Array.isArray(res) ? res : (res.cards || []);
};

export const createCard = async (data: Partial<Card>): Promise<{ id: number; message: string }> => {
  return post('/cards', data);
};

export const updateCard = async (cardId: string, data: Partial<Card>): Promise<ApiResponse> => {
  return put(`/cards/${cardId}`, data);
};

export const deleteCard = async (cardId: string): Promise<ApiResponse> => {
  return del(`/cards/${cardId}`);
};

export const getCardDetails = async (cardId: string): Promise<any> => {
  return get(`/cards/${cardId}/details`);
};

// Items
export const getItems = async (): Promise<Item[]> => {
    const res = await get<any>('/items');
    const items = Array.isArray(res) ? res : (res.items || []);
    return items.map(normalizeItem);
}

export const syncItemsFromAccount = async (cookieId: string): Promise<any> => {
    return post(`/accounts/${cookieId}/items/sync`, {});
}

export const deleteItem = async (cookieId: string, itemId: string): Promise<any> => {
    return del(`/items/${cookieId}/${itemId}`);
}

export const createItem = async (cookieId: string, data: any): Promise<any> => {
    return post(`/items/${cookieId}`, data);
}

export const updateItem = async (cookieId: string, itemId: string, data: any): Promise<any> => {
    return put(`/items/${cookieId}/${itemId}`, data);
}

export const updateItemMultiSpec = async (cookieId: string, itemId: string, enabled: boolean): Promise<any> => {
    return put(`/items/${cookieId}/${itemId}/multi-spec`, { is_multi_spec: enabled });
}

export const updateItemMultiQuantityDelivery = async (cookieId: string, itemId: string, enabled: boolean): Promise<any> => {
    return put(`/items/${cookieId}/${itemId}/multi-quantity-delivery`, { multi_quantity_delivery: enabled });
}

export const updateItemAutoRelist = async (cookieId: string, itemId: string, enabled: boolean): Promise<any> => {
    return put(`/items/${cookieId}/${itemId}/auto-relist`, { auto_relist_enabled: enabled });
}

// Item card bindings - 自动发货商品绑定
export const getItemCardBindings = async (cookieId?: string): Promise<ItemCardBinding[]> => {
    const res = await get<any>('/item-card-bindings', cookieId ? { cookie_id: cookieId } : undefined);
    const bindings = Array.isArray(res) ? res : (res.bindings || []);
    return bindings.map(normalizeItemCardBinding);
}

export const upsertItemCardBinding = async (data: {
    cookie_id: string;
    item_id: string;
    card_id: number;
    enabled?: boolean;
    description?: string;
}): Promise<{ message?: string; binding: ItemCardBinding }> => {
    const res = await post<any>('/item-card-bindings', data);
    return { ...res, binding: normalizeItemCardBinding(res.binding) };
}

export const updateItemCardBinding = async (
    bindingId: string | number,
    data: Partial<{
        card_id: number;
        enabled: boolean;
        description: string;
    }>
): Promise<{ message?: string; binding: ItemCardBinding }> => {
    const res = await put<any>(`/item-card-bindings/${bindingId}`, data);
    return { ...res, binding: normalizeItemCardBinding(res.binding) };
}

export const deleteItemCardBinding = async (bindingId: string | number): Promise<any> => {
    return del(`/item-card-bindings/${bindingId}`);
}

// Rules - 发货规则 (使用正确的后端API)
export const getShippingRules = async (): Promise<ShippingRule[]> => {
    const res = await get<any>('/delivery-rules');
    const rules = Array.isArray(res) ? res : (res.data || res.rules || []);
    // 转换后端数据格式到前端格式
    return rules.map((item: any) => ({
        id: String(item.id),
        name: item.description || item.keyword || '',
        item_keyword: item.keyword || '',
        card_group_id: item.card_id || 0,
        card_group_name: item.card_name || '',
        priority: item.delivery_count || 1,
        enabled: item.enabled || false
    }));
}

export const updateShippingRule = async (rule: Partial<ShippingRule>): Promise<any> => {
    const payload = {
        keyword: rule.item_keyword,
        card_id: rule.card_group_id,
        delivery_count: rule.priority,
        enabled: rule.enabled ?? true,
        description: rule.name
    };
    return rule.id ? put(`/delivery-rules/${rule.id}`, payload) : post('/delivery-rules', payload);
}

export const deleteShippingRule = async (id: string): Promise<any> => del(`/delivery-rules/${id}`);

// Rules - 关键词回复规则 (使用关键词API)
export const getReplyRules = async (cookieId?: string): Promise<ReplyRule[]> => {
    if (!cookieId) return [];
    const res = await get<any>(`/keywords-with-item-id/${cookieId}`);
    const keywords = Array.isArray(res) ? res : [];
    return keywords.map((item: any, index: number) => ({
        id: String(index),
        keyword: item.keyword || '',
        reply_content: item.reply || '',
        match_type: 'exact' as const,
        enabled: true
    }));
}

export const updateReplyRule = async (rule: Partial<ReplyRule>, cookieId: string): Promise<any> => {
    // 获取现有关键词
    const existing = await get<any>(`/keywords-with-item-id/${cookieId}`);
    const keywords = Array.isArray(existing) ? existing : [];

    // 更新或添加关键词
    if (rule.id) {
        const index = parseInt(rule.id);
        if (index >= 0 && index < keywords.length) {
            keywords[index] = {
                keyword: rule.keyword,
                reply: rule.reply_content,
                item_id: ''
            };
        }
    } else {
        keywords.push({
            keyword: rule.keyword,
            reply: rule.reply_content,
            item_id: ''
        });
    }

    return post(`/keywords-with-item-id/${cookieId}`, { keywords });
}

export const deleteReplyRule = async (id: string, cookieId: string): Promise<any> => {
    const existing = await get<any>(`/keywords-with-item-id/${cookieId}`);
    const keywords = Array.isArray(existing) ? existing : [];
    const index = parseInt(id);
    if (index >= 0 && index < keywords.length) {
        keywords.splice(index, 1);
    }
    return post(`/keywords-with-item-id/${cookieId}`, { keywords });
}

// Settings
export const getSystemSettings = async (): Promise<SystemSettings> => {
    const res = await get<{data: SystemSettings}>('/system-settings');
    return res.data || res; // handle {success:true, data: {...}} wrapper if exists
};

export const updateSystemSettings = async (settings: Partial<SystemSettings>): Promise<ApiResponse> => {
    // API expects individual PUTs, but we'll loop in the service for convenience or assume bulk endpoint if updated
    // Based on docs 12.2, we iterate.
    const promises = Object.entries(settings).map(([key, value]) => {
         return put(`/system-settings/${key}`, { value: String(value) });
    });
    await Promise.all(promises);
    return { success: true, message: 'Settings saved' };
};

export const getAccountAISettings = async (cookieId: string): Promise<AIReplySettings> => {
    return get(`/ai-reply-settings/${cookieId}`);
}

export const updateAccountAISettings = async (cookieId: string, settings: Partial<AIReplySettings>): Promise<ApiResponse> => {
  const payload = {
    ai_enabled: settings.ai_enabled ?? false,
    model_name: settings.model_name ?? 'qwen-plus',
    api_key: settings.api_key ?? '',
    base_url: settings.base_url ?? 'https://dashscope.aliyuncs.com/compatible-mode/v1',
    max_discount_percent: settings.max_discount_percent ?? 10,
    max_discount_amount: settings.max_discount_amount ?? 100,
    max_bargain_rounds: settings.max_bargain_rounds ?? 3,
    custom_prompts: settings.custom_prompts ?? ''
  };
  return put(`/ai-reply-settings/${cookieId}`, payload);
}

export const testAIConnection = async (cookieId: string): Promise<ApiResponse> => {
  const result = await post<{ success?: boolean; message?: string; reply?: string }>(`/ai-reply-test/${cookieId}`, {
    message: '你好，这是一条测试消息',
  });
  if (result.reply) {
    return { success: true, message: `AI 回复: ${result.reply}` };
  }
  return { success: result.success ?? true, message: result.message || 'AI 连接测试成功' };
}

// Notification Channels
export const getNotificationChannels = async (): Promise<{ success: boolean; data?: any[] }> => {
  const result = await get<any[]>('/notification-channels');
  const channels = (result || []).map((item: any) => {
    let parsedConfig;
    try {
      parsedConfig = JSON.parse(item.config);
    } catch {
      parsedConfig = undefined;
    }
    return {
      id: String(item.id),
      name: item.name,
      type: item.type,
      config: parsedConfig,
      enabled: item.enabled,
      created_at: item.created_at,
      updated_at: item.updated_at,
    };
  });
  return { success: true, data: channels };
}

export const createNotificationChannel = async (data: { name: string; type: string; config: Record<string, unknown> }): Promise<ApiResponse> => {
  return post('/notification-channels', {
    ...data,
    config: JSON.stringify(data.config)
  });
}

export const updateNotificationChannel = async (channelId: string, data: { name?: string; config?: Record<string, unknown>; enabled?: boolean }): Promise<ApiResponse> => {
  const payload: Record<string, unknown> = { ...data };
  if ('config' in data) {
    payload.config = JSON.stringify(data.config);
  }
  return put(`/notification-channels/${channelId}`, payload);
}

export const deleteNotificationChannel = async (channelId: string): Promise<ApiResponse> => {
  return del(`/notification-channels/${channelId}`);
}

// Message Notifications
export const getMessageNotifications = async (): Promise<{ success: boolean; data?: any[] }> => {
  const result = await get<Record<string, any[]>>('/message-notifications');
  const notifications = [];
  for (const [cookieId, channelList] of Object.entries(result || {})) {
    if (Array.isArray(channelList)) {
      for (const item of channelList) {
        notifications.push({
          cookie_id: cookieId,
          channel_id: item.channel_id,
          channel_name: item.channel_name,
          enabled: item.enabled,
        });
      }
    }
  }
  return { success: true, data: notifications };
}

export const setMessageNotification = async (cookieId: string, channelId: number, enabled: boolean): Promise<ApiResponse> => {
  return post(`/message-notifications/${cookieId}`, { channel_id: channelId, enabled });
}

export const deleteMessageNotification = async (notificationId: string): Promise<ApiResponse> => {
  return del(`/message-notifications/${notificationId}`);
}

export const deleteAccountNotifications = async (cookieId: string): Promise<ApiResponse> => {
  return del(`/message-notifications/account/${cookieId}`);
}

// Default Reply
export const getDefaultReplies = async (): Promise<Record<string, DefaultReply>> => {
  return get('/api/default-replies');
};

export const getDefaultReply = async (cookieId: string): Promise<DefaultReply> => {
  const result = await get<any>(`/api/default-reply/${cookieId}`);
  return {
    cookie_id: cookieId,
    enabled: result.enabled || false,
    reply_content: result.reply_content || '',
    reply_once: result.reply_once || false,
    reply_image_url: result.reply_image_url || ''
  };
};

export const updateDefaultReply = async (cookieId: string, data: Partial<DefaultReply>): Promise<ApiResponse> => {
  return put(`/api/default-reply/${cookieId}`, {
    enabled: data.enabled ?? false,
    reply_content: data.reply_content || '',
    reply_once: data.reply_once ?? false,
    reply_image_url: data.reply_image_url || ''
  });
};

export const deleteDefaultReply = async (cookieId: string): Promise<ApiResponse> => {
  return del(`/api/default-reply/${cookieId}`);
};

export const clearDefaultReplyRecords = async (cookieId: string): Promise<ApiResponse> => {
  return post(`/api/default-reply/${cookieId}/clear-records`, {});
};
